import React, { useState, useEffect, memo, useCallback } from "react";
import clsx from "clsx";

import { makeStyles, Divider, ButtonBase } from "@material-ui/core";
import { CloseIcon, MenuIcon } from "../icons";
import filters from "./routeFilters";
import { Text, View } from "../Base";
import { FILTER } from "../../constants/strings";
import palette from "../styles/palette";

const useStyles = makeStyles(() => ({
  container: {
    width: "16.5rem",
    background: palette.slate,
    transform: "translateX(16.5rem)",
    right: 0,
    top: 0,
    bottom: 0,
    display: "flex",
    position: "absolute",
    flexDirection: "column",
    transition: "transform .2s",
  },
  containerOpened: {
    transform: "translateX(0)",
  },
  header: {
    fontSize: "1.375rem",
    lineHeight: "1.625rem",
    margin: "0.938rem 0 0.25rem",
    color: palette.typography.white.solid,
    paddingLeft: "1.25rem",
  },
  divider: {
    height: "0.063rem",
    background: palette.typography.white.ghost,
  },
  list: {
    paddingLeft: "1.25rem",
    overflowY: "auto",
  },
  title: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: "1.25rem",
    color: palette.yellow,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    paddingTop: "1.125rem",
    paddingBottom: "0.5rem",
  },
  content: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  button: {
    color: palette.typography.white.solid,
    fontSize: "0.688rem",
    lineHeight: "0.813rem",
    padding: "0.125rem 0.25rem",
    border: "0.063rem solid white",
    borderRadius: "0.313rem",
    margin: "0.625rem 0.875rem 0 0",
    letterSpacing: "0.0025em",
    cursor: "pointer",
    fontFamily: "Roboto",
  },
  buttonActive: {
    color: palette.typography.black.high,
    background: palette.typography.white.solid,
  },
  menuButton: {
    width: "2.125rem",
    height: "2.5rem",
    position: "absolute",
    left: "-2.125rem",
    background: palette.slate,
    top: "calc(50% - 1.25rem)",
    color: palette.typography.white.solid,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: "0.313rem",
    borderBottomLeftRadius: "0.313rem",
    boxShadow: "0 0.25rem 0.25rem rgba(0, 0, 0, 0.25)",
    cursor: "pointer",

    "& svg": {
      height: "1rem",
    },
  },
}));

const Button = ({ children, active, onClick }) => {
  const styles = useStyles();

  return (
    <ButtonBase
      className={clsx(styles.button, active && styles.buttonActive)}
      onClick={onClick}
    >
      {children}
    </ButtonBase>
  );
};
const Section = ({ values, selectedValues = [], title, field, onChange }) => {
  const styles = useStyles();

  const handleOnClick = useCallback(
    (value, label) => {
      if (!selectedValues.length && !value) return;

      if (!value) {
        return onChange(field, label, []);
      }

      let result;
      if (selectedValues.includes(value)) {
        result = selectedValues.filter((v) => v !== value);
      } else {
        result = [...selectedValues, value];
      }
      onChange(field, label, result);
    },
    [field, selectedValues, onChange]
  );

  return (
    <View className={styles.section}>
      <Text className={styles.title}>{title}</Text>
      <View className={styles.content}>
        {values.map(({ label, value }) => (
          <Button
            key={label + value}
            onClick={() => handleOnClick(value, label)}
            active={
              selectedValues.includes(value) ||
              (!selectedValues.length && !value)
            }
          >
            {label}
          </Button>
        ))}
      </View>
    </View>
  );
};

const FilterPanel = ({
  filter,
  detailVisible,
  onChange,
  onClickFilterPanelButton,
  onLoad,
}) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    open && onLoad && onLoad();
  }, [open, onLoad]);

  useEffect(() => {
    detailVisible && setOpen(false);
  }, [detailVisible]);

  const handleOnClickFilterButton = useCallback(() => {
    setOpen(!open);
    onClickFilterPanelButton && onClickFilterPanelButton(open);
  }, [open, onClickFilterPanelButton]);

  return (
    <View className={clsx(styles.container, open && styles.containerOpened)}>
      <View
        className={clsx(styles.menuButton, open && styles.closed)}
        onClick={handleOnClickFilterButton}
        data-testid="open-close-route-list-filter"
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </View>
      <Text className={styles.header}>{FILTER}</Text>
      <Divider className={styles.divider} />
      <View className={styles.list}>
        {filters.map(({ title, field, values }, id) => (
          <Section
            key={`${title}_${id}`}
            selectedValues={filter[field]}
            field={field}
            title={title}
            values={values}
            onChange={onChange}
          />
        ))}
      </View>
    </View>
  );
};

export default memo(FilterPanel);
